import { series, AsyncFunction } from 'async-es';
import React, { useCallback, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Classes } from '@blueprintjs/core';
import { faCopy, faFileExport } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment-timezone';

import { Menu, MenuItem } from '../menu';
import Modal from '../modal';
import Popover from '../popover';
import Dialog from '../dialog';
import Toast from '../toast';
import Button from '../../molecules/button';
import Icon from '../../atoms/icon';
import BPCheckbox from '../../atoms/checkbox/bpcheckbox';

import { useApps } from '../../../contexts/apps';
import { AppKey } from '../../../libs/apps';
import { DownloadBlobAsFile } from '../../../libs/file';
import {
  BaseResponse,
  PostJSON,
  PostBlob,
  ErrorResponse
} from '../../../libs/request';
import { FormatDate, GetCurrentTZ } from '../../../libs/time';
import useRequestJSON from '../../../libs/hooks/useRequestJSON';

/**
 * Utils
 */

const getGraphB64 = (graph: any, graphs: string[]) => {
  return (cb: any) => {
    PostJSON<string>({
      url: 'https://hcexport.lightbeamhealth.com/',
      params: {
        type: 'image/png',
        scale: 2,
        svg: graph.getSVG(),
        b64: true
      }
    })
      .then((response) => {
        if (typeof response !== 'string') {
          console.error('Error generating graph:', response.message);
          cb(); // Don't return error, just continue
          return;
        }
        graphs.push(response);
        if (cb) {
          cb();
        }
        return;
      })
      .catch((err) => {
        console.error('Error generating graph:', err);
        cb(); // Don't return error, just continue
        return;
      });
  };
};

/**
 * Hooks
 */

interface GetExportTextResponse extends BaseResponse {
  body: string;
}

interface UseGetExportTextValue {
  clipboardData: string;
}

const useGetExportText = ({
  appKey,
  patientId,
  start,
  end,
  showAlerts,
  showTimes,
  useTabs,
  programId = null
}): UseGetExportTextValue => {
  const request = useCallback(
    () =>
      PostJSON({
        url: '/data/export_text/',
        params: {
          app: appKey,
          tz: GetCurrentTZ(),
          patientId,
          start,
          end,
          showAlerts,
          showTimes,
          useTabs,
          programId
        }
      }),
    [appKey, patientId, start, end, showAlerts, showTimes, useTabs]
  );
  const { data } = useRequestJSON<GetExportTextResponse, GetExportTextResponse>(
    {
      request
    }
  );
  return {
    clipboardData: data && data.body ? data.body : null
  };
};

const useExportGraphsAsPDF = ({
  graphs,
  start,
  end,
  appKey,
  patientId,
  setGraphInstances,
  programId = null
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportAsPDF = () => {
    if (!graphs) {
      return;
    }
    if (isExporting) {
      return;
    }
    setIsExporting(true);
    const tasks: AsyncFunction<any, Error>[] = [];
    const graphB64s: string[] = [];
    for (const graph of graphs) {
      graph.setTitle({ text: null });
      graph.options.exporting = {
        sourceWidth: 648,
        sourceHeight: Math.floor((450 - 32) / graphs.length)
      };
      tasks.push(getGraphB64(graph, graphB64s));
    }
    tasks.push((cb) => {
      const params = {
        graphs: graphB64s,
        app: appKey,
        tz: GetCurrentTZ(),
        start,
        end,
        patientId,
        programId
      };
      PostBlob<Blob>({
        url: '/data/export_pdf/',
        params
      }).then((response) => {
        if (!(response instanceof Blob)) {
          if ((response as ErrorResponse).status === 'FAIL') {
            Modal.error({
              title: 'Error Exporting',
              content: (response as ErrorResponse).message
            });
          }
          cb();
          return;
        }
        // PDF blob
        const filename = moment().format('YYYYMMDDhhmmss') + '.pdf';
        DownloadBlobAsFile(response as Blob, filename);
        cb();
        return;
      });
    });
    series(tasks, (err: Error) => {
      setIsExporting(false);
      if (err) {
        Modal.error({
          title: 'Error Exporting As PDF',
          content: err.message
        });
        return;
      }
      Modal.success({
        title: 'Export Successful',
        content:
          'If you are not seeing the export download, please make sure you are using a modern browser and the browser software is up to date.'
      });
      if (setGraphInstances) {
        setGraphInstances(graphs);
      }
      return;
    });
  };

  return {
    isExporting,
    exportAsPDF
  };
};

/**
 * Components
 */

interface ExportTextDialogProps {
  isOpen: boolean;
  start: string;
  end: string;
  appKey: AppKey;
  patientId: number;
  setIsOpen: (isOpen: boolean) => void;
  programId?: number;
}

// TODO: Allow user to save text presets
const ExportTextDialog: React.FC<ExportTextDialogProps> = ({
  isOpen,
  setIsOpen,
  start,
  end,
  appKey,
  patientId,
  programId = null
}) => {
  const { data } = useApps();
  const app = data ? data.apps[appKey] : undefined;
  const [showAlerts, setShowAlerts] = useState(true);
  const [showTimes, setShowTimes] = useState(true);
  const [useTabs, setUseTabs] = useState(false);
  const { clipboardData } = useGetExportText({
    appKey,
    patientId,
    start,
    end,
    showAlerts,
    showTimes,
    useTabs,
    programId
  });
  return (
    <Dialog isOpen={isOpen} maxWidth="1024px" onClose={() => setIsOpen(false)}>
      <div className={Classes.DIALOG_HEADER}>
        <h3>Export As Text</h3>
      </div>
      <div className={Classes.DIALOG_BODY}>
        <p>
          Toggle the checkboxes below to update the copyable text. Click{' '}
          <strong>Copy</strong> to copy the text to your clipboard for pasting
          elsewhere.
        </p>
        <div>
          <BPCheckbox
            label="Show Alerts"
            checked={showAlerts}
            onClick={() => setShowAlerts(!showAlerts)}
          />
          <BPCheckbox
            label="Show Message Time"
            checked={showTimes}
            onClick={() => setShowTimes(!showTimes)}
          />
          {app && app.exportTabs ? (
            <BPCheckbox
              label="Use Tabs Instead Of Borders"
              checked={useTabs}
              onClick={() => setUseTabs(!useTabs)}
            />
          ) : null}
        </div>
        <h4 className="mb-2">Preview</h4>
        {clipboardData && (
          <pre
            className="text-small"
            style={{
              maxHeight: '400px',
              overflowY: 'auto'
            }}
          >
            {clipboardData}
          </pre>
        )}
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={() => setIsOpen(false)}>Cancel</Button>
          <CopyToClipboard
            text={clipboardData}
            onCopy={() =>
              Toast({
                message: 'Copied to clipboard!',
                intent: 'success',
                timeout: 2000
              })
            }
          >
            <Button intent="success" disabled={!clipboardData}>
              <Icon icon={faCopy} /> Copy
            </Button>
          </CopyToClipboard>
        </div>
      </div>
    </Dialog>
  );
};

interface ExportGraphDataProps {
  appKey: AppKey;
  patientId: number;
  start: Date;
  end: Date;
  graphs: any[];
  setGraphInstances?: (instances: any) => void;
  programId?: number | null;
}

const ExportGraphData: React.FC<ExportGraphDataProps> = ({
  appKey,
  patientId,
  graphs,
  start,
  end,
  setGraphInstances,
  programId = null
}) => {
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const startDate = FormatDate(start, 'YYYY-MM-DD');
  const endDate = FormatDate(end, 'YYYY-MM-DD');
  const { exportAsPDF, isExporting } = useExportGraphsAsPDF({
    graphs,
    start: startDate,
    end: endDate,
    setGraphInstances,
    appKey,
    patientId,
    programId
  });
  const dumpKey = programId ? 'composer' : appKey;

  const exportAsCSV = useCallback(() => {
    const query = new URLSearchParams({
      patient: String(patientId),
      ...(programId ? { program: String(programId) } : {})
    }).toString();

    PostBlob<Blob>({
      url: `/${dumpKey}/dump/?${query}`
    })
      .then((response) => {
        if (response instanceof Blob) {
          const filename = `${dumpKey}_${patientId}_${moment().format(
            'YYYYMMDD'
          )}.csv`;
          DownloadBlobAsFile(response, filename);
        } else {
          Modal.error({
            title: 'Error Exporting CSV',
            content:
              (response as ErrorResponse).message || 'Unknown error occurred'
          });
        }
      })
      .catch((err) => {
        Modal.error({
          title: 'Error Exporting CSV',
          content: err.message
        });
      });
  }, [dumpKey, patientId, programId]);

  const menu = (
    <Menu>
      <MenuItem text="Export As PDF" onClick={exportAsPDF} />
      <MenuItem
        text="Export As Text"
        onClick={() => setIsTextModalOpen(true)}
      />
      {appKey && patientId && (
        <MenuItem text="Export As CSV" onClick={exportAsCSV} />
      )}
    </Menu>
  );
  return (
    <React.Fragment>
      <ExportTextDialog
        isOpen={isTextModalOpen}
        setIsOpen={setIsTextModalOpen}
        start={startDate}
        end={endDate}
        appKey={appKey}
        patientId={patientId}
        programId={programId}
      />
      <Popover content={menu}>
        <Button intent="primary" disabled={isExporting}>
          <Icon icon={faFileExport} className="mr-3" />
          {isExporting ? 'Exporting...' : 'Export Report'}
        </Button>
      </Popover>
    </React.Fragment>
  );
};

export default ExportGraphData;
